import React, { useState, useEffect } from 'react';
import {
  Camera, Brain, User, LogOut, Edit, Save, X, Link,
  Twitter, ExternalLink, Globe, CheckCircle
} from 'lucide-react';
import ProfileCreation from './ProfileCreation';
import LiveSession from './LiveSession';
import { useAuth } from '../hooks/useAuth';
import AdPlaceholder from './AdPlaceholder';

// === CONFIGURAÇÕES X OAUTH ===
const X_OAUTH_CLIENT_ID = process.env.X_CLIENT_ID;
const REDIRECT_URI = 'https://network.plexonatural.com/oauth/x/callback';

const PlexoNatural = () => {
  const { user, login, logout } = useAuth();

  // === ESTADOS PRINCIPAIS ===
  const [isConnected, setIsConnected] = useState(false);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState({});
  const [showLiveSession, setShowLiveSession] = useState(false);
  const [showLiveReport, setShowLiveReport] = useState({
    show: false,
    tokensEarned: 0,
    feedback: '',
    clipUrl: null,
    txHash: null,
    polygonScanUrl: null,
    proofResult: null
  });

  // === GERAR USERNAME AUTOMÁTICO ===
  const generateUsername = (name) => {
    if (!name) return '';
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .substring(0, 15);
  };

  // === SINCRONIZAR PERFIL COM useAuth ===
  useEffect(() => {
    // Priorizar user do useAuth (fonte de verdade)
    if (user) {
      console.log('✅ Usuário do useAuth encontrado:', user.userId);
      setCurrentProfile(user);
      setIsConnected(true);

      // Sincronizar localStorage com user do useAuth
      if (user.token) {
        localStorage.setItem('token', user.token);
        localStorage.setItem('userData', JSON.stringify(user));
      }
    } else {
      // Se não tem user no useAuth, verificar localStorage (fallback)
      const saved = localStorage.getItem('userData');
      const token = localStorage.getItem('token');

      if (saved && token && token !== 'undefined' && token !== 'null') {
        try {
          const parsed = JSON.parse(saved);
          console.log('⚠️ Usando fallback do localStorage:', parsed.userId);
          setCurrentProfile(parsed);
          setIsConnected(true);
        } catch (err) {
          console.error('Erro ao parsear userData:', err);
          localStorage.removeItem('userData');
          localStorage.removeItem('token');
          setCurrentProfile(null);
          setIsConnected(false);
        }
      } else {
        // Nenhum usuário encontrado
        setCurrentProfile(null);
        setIsConnected(false);
      }
    }
  }, [user]);

  // === LOG NO RENDER (DIAGNÓSTICO) ===
  console.log('RENDERIZANDO COM currentProfile:', currentProfile);

  // === CRIAR PERFIL ===
  const handleProfileCreated = (profileData, token) => {
    console.log('✅ Perfil criado, fazendo login...', { userId: profileData.userId, hasToken: !!token });

    // Garantir que token está no profileData
    const profileWithToken = { ...profileData, token };

    // Salvar no localStorage
    localStorage.setItem('token', token);
    localStorage.setItem('userData', JSON.stringify(profileWithToken));

    // Atualizar useAuth (fonte de verdade)
    login(profileWithToken, token);

    // Atualizar estado local (será sincronizado pelo useEffect)
    setCurrentProfile(profileWithToken);
    setIsConnected(true);

    console.log('✅ Login completo, usuário conectado');
  };

  // === LOGOUT ===
  const handleLogout = () => {
    console.log('🚪 Iniciando logout...');

    // Limpar estados locais primeiro
    setCurrentProfile(null);
    setIsConnected(false);
    setShowLiveSession(false);
    setShowLiveReport({ ...showLiveReport, show: false });

    // Chamar logout do useAuth (que já limpa localStorage e redireciona)
    logout();
  };

  // === X OAUTH: INICIAR LOGIN ===
  const startXOAuth = () => {
    const state = btoa(JSON.stringify({ userId: currentProfile.userId }));
    const url = `https://twitter.com/i/oauth2/authorize?` +
      `response_type=code&` +
      `client_id=${process.env.X_CLIENT_ID}&` +
      `redirect_uri=${encodeURIComponent(REDIRECT_URI)}&` +
      `scope=tweet.write%20users.read&` +
      `state=${state}&` +
      `code_challenge=challenge&` +
      `code_challenge_method=plain`;
    window.location.href = url;
  };

  // === submitProofX: POSTAR NO X (VIA SEU BACKEND) ===
  const submitProofX = async (txHash, clipUrl) => {
    const message = `Proof of Life/Existence na @PlexoNatural\n\n` +
      `Tx: ${txHash}\n` +
      `Clip: ${clipUrl}\n\n` +
      `with Grok services · Deus no centro`;

    const token = localStorage.getItem('x_access_token');
    if (!token) {
      alert('Faça login no X para publicar a prova');
      startXOAuth();
      return;
    }

    try {
      const res = await fetch('https://plexo-natural-api.onrender.com/oauth/x/tweet', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}` // ← JWT do Plexo (opcional)
        },
        body: JSON.stringify({
          text: message,
          x_access_token: token  // ← ENVIA O TOKEN DO X
        })
      });

      if (res.ok) {
        alert('Prova publicada no X com sucesso!');
      } else {
        const err = await res.json();
        throw new Error(err.error || 'Erro ao postar');
      }
    } catch (err) {
      console.error('Erro no submitProofX:', err);
      alert('Erro ao postar: ' + err.message);
    }
  };

  // === handleLiveComplete ===
  const handleLiveComplete = async (liveData) => {
    console.log('🎯 handleLiveComplete INICIADO com:', liveData);
    try {

      // Se há erro, mostrar e sair
      if (liveData.error) {
        console.error('❌ Erro no liveData:', liveData.error);
        alert(`Erro: ${liveData.error}`);
        setShowLiveSession(false);
        setIsLive(false);
        setShowLiveReport({ ...showLiveReport, show: false });
        return;
      }

      // Garantir que sempre mostra o card de sucesso se a live foi processada
      if (liveData.alreadyUploaded || liveData.processedByLiveSession || liveData.success || liveData.frameUrl) {
        const newLive = {
          duration: 60,
          timestamp: new Date().toISOString(),
          tokensEarned: liveData.tokensEarned || 10,
          feedback: liveData.feedback || 'Ótima energia!',
          frameUrl: liveData.frameUrl,
          uploadMethod: liveData.uploadMethod || 'pinata',
          txHash: liveData.txHash || null
        };
        const updatedUser = {
          ...currentProfile,
          tokens: liveData.totalTokens || (currentProfile.tokens + (liveData.tokensEarned || 10)),
          lastLive: new Date().toISOString(),
          liveHistory: [...(currentProfile.liveHistory || []), newLive].slice(-3)
        };
        setCurrentProfile(updatedUser);
        localStorage.setItem('userData', JSON.stringify(updatedUser));
        // Sempre mostrar card de sucesso
        setShowLiveReport({
          show: true,
          tokensEarned: liveData.tokensEarned || 10,
          feedback: liveData.feedback || 'Ótima energia! Mantenha o foco visual.',
          clipUrl: liveData.frameUrl || null,
          txHash: liveData.txHash || null,
          polygonScanUrl: liveData.polygonScanUrl || null,
          proofResult: liveData.proofResult || null
        });
        setShowLiveSession(false);
        setIsLive(false);
        console.log('✅ Card de sucesso configurado!');
        return;
      }
      // CASO ANTIGO (upload manual)
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token não encontrado');
      const dataToSend = {
        duration: liveData.duration || 60,
        frame: liveData.clip,
        timestamp: liveData.timestamp || new Date().toISOString()
      };
      const response = await fetch('https://plexo-natural-api.onrender.com/api/complete-live', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSend)
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      const updatedUser = {
        ...currentProfile,
        tokens: result.totalTokens,
        lastLive: result.profile?.lastLive || new Date().toISOString(),
        liveHistory: result.profile?.liveHistory || [...(currentProfile.liveHistory || []), {
          duration: 60,
          timestamp: new Date().toISOString(),
          tokensEarned: result.tokensEarned,
          feedback: result.feedback,
          frameUrl: result.frameUrl,
          uploadMethod: result.uploadMethod
        }].slice(-3)
      };
      setCurrentProfile(updatedUser);
      localStorage.setItem('userData', JSON.stringify(updatedUser));
      setShowLiveReport({
        show: true,
        tokensEarned: result.tokensEarned,
        feedback: result.feedback,
        clipUrl: result.frameUrl,
        txHash: null,
        polygonScanUrl: null
      });
      setShowLiveSession(false);
      setIsLive(false);
    } catch (error) {
      console.error('Erro ao processar live:', error);
      alert(`Erro: ${error.message}`);
      setShowLiveSession(false);
      setIsLive(false);
    }
  };
  const closeLiveReport = () => {
    setShowLiveReport({ ...showLiveReport, show: false });
  };
  const startLiveSession = () => {
    setShowLiveSession(true);
    setIsLive(true);
    setShowLiveReport({ ...showLiveReport, show: false });
  };
  const cancelLiveSession = () => {
    setShowLiveSession(false);
    setIsLive(false);
  };
  // === EDIÇÃO DE PERFIL ===
  const startEditing = () => {
    setIsEditing(true);
    setEditedProfile({
      name: currentProfile?.profileData?.name || '',
      username: currentProfile?.profileData?.username || '',
      bio: currentProfile?.profileData?.bio || '',
      profileImage: currentProfile?.profileData?.profileImage || ''
    });
  };
  const cancelEditing = () => {
    setIsEditing(false);
    setEditedProfile({});
  };
  const handleInputChange = (field, value) => {
    setEditedProfile(prev => ({ ...prev, [field]: value }));
  };
  const handleGenerateUsername = () => {
    const generated = generateUsername(editedProfile.name);
    setEditedProfile(prev => ({ ...prev, username: generated }));
  };
  const saveProfile = async () => {
    try {
      const updatedData = {
        profileData: {
          name: editedProfile.name,
          username: editedProfile.username,
          bio: editedProfile.bio,
          profileImage: editedProfile.profileImage
        }
      };
      const token = localStorage.getItem('token');
      const response = await fetch('https://plexo-natural-api.onrender.com/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatedData)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      const result = await response.json();
      if (result.success) {
        const updatedUser = { ...currentProfile, ...result.profile };
        setCurrentProfile(updatedUser);
        localStorage.setItem('userData', JSON.stringify(updatedUser));
        setIsEditing(false);
        if (editedProfile.username) {
          alert(`Perfil atualizado!\n\nSua URL: network.plexonatural.com/${editedProfile.username}`);
        } else {
          alert('Perfil atualizado com sucesso!');
        }
      }
    } catch (error) {
      alert(`Erro: ${error.message}`);
    }
  };
  // === RENDER: SEM PERFIL ===
  if (!currentProfile) {
    return (
      <div className="min-h-screen bg-white">
        <header className="bg-white shadow-sm border-b-2 border-violet-200">
          <div className="max-w-4xl mx-auto px-4 py-4 text-center">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-violet-600 to-green-500 bg-clip-text text-transparent">
              Plexo Natural
            </h1>
            <p className="text-xs text-gray-600">ML-Powered Creative Network</p>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-8">
          <ProfileCreation onProfileCreated={handleProfileCreated} />
        </main>
      </div>
    );
  }
  // === RENDER: LIVE ATIVA ===
  if (showLiveSession) {
    return (
      <div className="min-h-screen bg-gray-900">
        <header className="bg-red-600 text-white p-4">
          <div className="max-w-4xl mx-auto flex justify-between items-center">
            <h1 className="text-xl font-bold flex items-center">
              <Camera className="w-6 h-6 mr-2" /> Sessão Ao Vivo
            </h1>
            <button onClick={cancelLiveSession} className="bg-white text-red-600 px-4 py-2 rounded-lg font-bold">
              Sair
            </button>
          </div>
        </header>
        <main className="max-w-4xl mx-auto p-4">
          <LiveSession onComplete={handleLiveComplete} />
        </main>
      </div>
    );
  }
  // === RENDER: PRINCIPAL ===
  return (
    <div className="min-h-screen bg-white">
      {/* === CARD FINAL COM txHash === */}
      {showLiveReport.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Live Concluída!</h3>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="text-3xl font-bold text-green-600">+{showLiveReport.tokensEarned} tokens</div>
                <div className="text-green-700">Total: {currentProfile?.tokens}</div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-left">
                <h4 className="font-bold text-blue-800 mb-1 flex items-center gap-2">
                  Native Heuristic Insight
                </h4>
                <p className="text-blue-700 text-sm italic">{showLiveReport.feedback}</p>
                <div className="mt-2 text-[10px] text-blue-400">PlexoNatural specialized v2.0 engine</div>
              </div>
              {showLiveReport.clipUrl && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                  <p className="font-bold text-purple-800 mb-1">Clip Salvo:</p>
                  <a href={showLiveReport.clipUrl} target="_blank" className="text-purple-600 underline text-sm flex items-center">
                    <ExternalLink className="w-4 h-4 mr-1" /> Ver clip
                  </a>
                </div>
              )}
              {showLiveReport.txHash && (
                <div className="bg-gradient-to-r from-violet-50 to-green-50 border-2 border-violet-200 rounded-lg p-4 mb-4">
                  <p className="font-bold text-violet-800 mb-1">Prova Eterna na Polygon:</p>
                  <a
                    href={showLiveReport.polygonScanUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-violet-600 hover:text-violet-800 font-mono text-sm break-all underline flex items-center"
                  >
                    <Globe className="w-4 h-4 mr-1" />
                    {showLiveReport.txHash}
                  </a>
                  <p className="text-xs text-gray-600 mt-2 italic">with Grok services · Deus no centro</p>
                </div>
              )}
              {showLiveReport.txHash && (
                <button
                  onClick={() => submitProofX(showLiveReport.txHash, showLiveReport.clipUrl)}
                  className="w-full mt-2 bg-gradient-to-r from-violet-600 to-green-500 text-white py-3 rounded-lg font-bold flex items-center justify-center hover:from-violet-700 hover:to-green-600 transition"
                >
                  <Twitter className="w-5 h-5 mr-2" />
                  Publicar Prova no X
                </button>
              )}
              {showLiveReport.txHash && (
                <a
                  href={`/decoder?tx=${showLiveReport.txHash}`}
                  className="block w-full mt-2 text-center bg-gradient-to-r from-violet-600 to-green-500 text-white py-3 rounded-lg font-bold hover:from-violet-700 hover:to-green-600 transition"
                >
                  Decodificar Prova
                </a>
              )}

              <div className="my-4 flex justify-center">
                <AdPlaceholder position="banner" />
              </div>
              <button
                onClick={closeLiveReport}
                className="w-full mt-4 bg-gray-800 text-white py-3 rounded-lg font-bold"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}
      {/* === MAIN: PERFIL === */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white border-2 border-violet-200 rounded-lg shadow-md p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Seu Perfil</h2>
            {!isEditing && (
              <button
                onClick={startEditing}
                className="flex items-center space-x-2 bg-gradient-to-r from-violet-600 to-green-500 hover:from-violet-700 hover:to-green-600 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Edit className="w-4 h-4" />
                <span>Editar Perfil</span>
              </button>
            )}
          </div>
          <div className="flex items-start space-x-6">
            <div className="flex-shrink-0 relative">
              <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                {currentProfile.profileData?.profileImage ? (
                  <img
                    src={currentProfile.profileData.profileImage}
                    alt={currentProfile.profileData.name}
                    className="w-24 h-24 rounded-full object-cover"
                  />
                ) : (
                  <User className="w-12 h-12 text-gray-400" />
                )}
              </div>
              {isEditing && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL da Foto:</label>
                  <input
                    type="text"
                    value={editedProfile.profileImage || ''}
                    onChange={(e) => handleInputChange('profileImage', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="Cole a URL da sua foto"
                  />
                </div>
              )}
            </div>
            <div className="flex-1">
              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome:</label>
                    <input
                      type="text"
                      value={editedProfile.name || ''}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Seu nome completo"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Link className="w-4 h-4 inline mr-1" />
                      Sua URL Pessoal:
                    </label>
                    <div className="flex space-x-2">
                      <div className="flex-1">
                        <div className="flex items-center text-sm text-gray-500 mb-1">
                          <span>network.plexonatural.com/</span>
                        </div>
                        <input
                          type="text"
                          value={editedProfile.username || ''}
                          onChange={(e) => handleInputChange('username', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="seu-usuario"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleGenerateUsername}
                        className="mt-6 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm transition-colors"
                      >
                        Gerar
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Esta será sua URL pessoal no Plexo Natural
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bio:</label>
                    <textarea
                      value={editedProfile.bio || ''}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Conte um pouco sobre você..."
                    />
                  </div>
                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={saveProfile}
                      className="flex items-center space-x-2 bg-gradient-to-r from-violet-600 to-green-500 hover:from-violet-700 hover:to-green-600 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      <Save className="w-4 h-4" />
                      <span>Salvar</span>
                    </button>
                    <button
                      onClick={cancelEditing}
                      className="flex items-center space-x-2 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" />
                      <span>Cancelar</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {currentProfile.profileData?.name}
                  </h3>
                  {currentProfile.profileData?.username && (
                    <div className="mb-3">
                      <span className="inline-flex items-center px-3 py-1 bg-violet-100 text-violet-800 rounded-full text-sm font-medium">
                        <Link className="w-3 h-3 mr-1" />
                        network.plexonatural.com/{currentProfile.profileData.username}
                      </span>
                    </div>
                  )}
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {currentProfile.profileData?.bio || 'Adicione uma bio para contar mais sobre você...'}
                  </p>
                  <div className="border-t pt-4">
                    <div className="flex items-center space-x-6 text-sm text-gray-600">
                      <div>
                        <span className="font-semibold text-gray-900">{currentProfile.tokens || 100}</span> tokens
                      </div>
                      <div>
                        <span className="font-semibold text-gray-900">{currentProfile.tier || 'Creator'}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-gray-500">
                          {isLive ? 'LIVE AGORA' : 'Offline'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* HISTÓRICO DE LIVES */}
          {currentProfile.liveHistory && currentProfile.liveHistory.length > 0 && (
            <div className="mt-8 border-t pt-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <Camera className="w-5 h-5 mr-2 text-red-500" />
                Histórico de Lives ({currentProfile.liveHistory.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {currentProfile.liveHistory.slice(-6).reverse().map((live, index) => (
                  <div key={index} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                    {live.frameUrl ? (
                      <div className="relative">
                        <video
                          src={live.frameUrl}
                          className="w-full h-40 object-cover"
                          controls
                          muted
                          preload="metadata"
                        />
                        <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs">
                          5s
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-40 bg-gray-300 flex items-center justify-center">
                        <Camera className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                    <div className="p-3 text-sm">
                      <div className="font-bold text-gray-800 mb-2">
                        Live #{currentProfile.liveHistory.length - index}
                      </div>
                      <div className="text-gray-600">
                        {Math.floor(live.duration / 60)}min {live.duration % 60}s
                      </div>
                      <div className="text-green-600 font-bold">
                        +{live.tokensEarned} tokens
                      </div>
                      <div className="text-xs text-gray-500 mt-2 italic">
                        "{live.feedback}"
                      </div>
                      <div className="text-xs text-gray-400 mt-2">
                        {new Date(live.timestamp).toLocaleString('pt-BR')}
                      </div>
                      {live.uploadMethod && (
                        <div className="text-xs text-blue-500 mt-1">
                          {live.uploadMethod}
                        </div>
                      )}
                      {live.txHash && (
                        <div className="text-xs text-teal-600 mt-1 font-mono">
                          Tx: {live.txHash.slice(0, 10)}...
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="mt-8 p-6 bg-gradient-to-r from-violet-50 to-green-50 rounded-lg border-2 border-violet-200">
            <h3 className="text-lg font-bold text-violet-800 mb-3 flex items-center">
              <Camera className="w-5 h-5 mr-2" />
              Sessões Ao Vivo v2.0
            </h3>
            <div className="space-y-2 text-sm text-gray-700">
              <p>Como funciona: Live de 1 minuto com clip automático de 5s aos 30s</p>
              <p>Recompensa: +10 tokens por live</p>
              <p>Feedback: Análise automática em tempo real</p>
              <p>Clips: Todos os clips de 5s salvos aparecem acima!</p>
              <p>URL Pessoal: Edite seu perfil para ter sua URL personalizada</p>
              <p>Sustentabilidade: Mantemos apenas 3 lives recentes</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
export default PlexoNatural;





